// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/form/report layout from the design system)
import { useState } from 'react';
import { Download, FileSpreadsheet, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import Modal from './Modal';
import { targetsAPI } from '../services/api';
import { downloadXlsx } from '../utils/download';
import { UploadErrorBanner } from './UploadErrorBanner';
import FileDropZone from './FileDropZone';

interface BulkUploadResult {
  success: number;
  updated: number;
  failed: number;
  errors: { row: number; subject: string; error: string }[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  projectId: number;
  scope: 'store' | 'employee';
  title?: string;
}

export default function TargetBulkUploadModal({ isOpen, onClose, onSuccess, projectId, scope, title }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [result, setResult] = useState<BulkUploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFile(null);
    setError(null);
    setResult(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const accept = (f: File): boolean => {
    if (!f.name.match(/\.(xlsx|xls|csv)$/i)) {
      setError('Please upload a valid Excel (.xlsx, .xls) or CSV file');
      return false;
    }
    return true;
  };

  const handleFile = (f: File) => {
    if (!accept(f)) return;
    setFile(f);
    setError(null);
    setResult(null);
  };

  const downloadTemplate = async () => {
    setIsDownloading(true);
    setError(null);
    try {
      const res = await targetsAPI.downloadTemplate({ projectId, scope });
      downloadXlsx(`targets_template_${scope}.xlsx`, res.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to download template');
    } finally {
      setIsDownloading(false);
    }
  };

  const upload = async () => {
    if (!file) {
      setError('Pick a file first');
      return;
    }
    setIsUploading(true);
    setError(null);
    setResult(null);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const base64 = (e.target?.result as string).split(',')[1];
        const res = await targetsAPI.bulkUpload(base64, projectId, scope);
        if (res.data.status === 'success') {
          setResult(res.data.data);
          if ((res.data.data.success ?? 0) > 0 || (res.data.data.updated ?? 0) > 0) {
            onSuccess();
          }
        } else {
          setError(res.data.message || 'Upload failed');
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to upload');
      } finally {
        setIsUploading(false);
      }
    };
    reader.onerror = () => {
      setError('Failed to read file');
      setIsUploading(false);
    };
    reader.readAsDataURL(file);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={title || `Bulk Upload — ${scope === 'store' ? 'Store-scoped' : 'Employee-scoped'}`}
      size="lg"
      footer={
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            onClick={close}
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              type="button"
              onClick={upload}
              disabled={!file || isUploading}
              // text-white belongs to the enabled branch only: left on the base it also sat
              // on the disabled button beside text-gray-500, and Tailwind emits text-white
              // later, so the label stayed white on the grey button.
              className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition-colors ${
                file && !isUploading ? 'bg-primary-600 text-white hover:bg-primary-700' : 'cursor-not-allowed bg-gray-300 text-gray-500'
              }`}
            >
              {isUploading && <Loader2 className="h-4 w-4 animate-spin" />}
              {isUploading ? 'Uploading…' : 'Upload'}
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-6">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-start gap-3">
            <FileSpreadsheet className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-blue-900">Download Template</h4>
              <p className="mt-1 text-sm text-blue-700">
                The template lists the active metrics for this project. Fill {scope === 'store' ? 'Store Code' : 'Employee Code'},
                metric, period (YYYY-MM), and at least one of <strong>Target Value</strong>, <strong>Achieved Value</strong>,
                or <strong>Achieved %</strong> per row. You don't need a target to record an achievement — leave Target blank
                and fill either Achieved Value (raw) or Achieved % (pre-computed).
              </p>
              <button
                onClick={downloadTemplate}
                disabled={isDownloading}
                className="mt-3 inline-flex items-center gap-2 rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
              >
                {isDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {isDownloading ? 'Downloading…' : 'Download Template'}
              </button>
            </div>
          </div>
        </div>

        {!result && (
          <FileDropZone
            inputId="target-bulk-upload-file"
            file={file}
            onFile={handleFile}
            onClear={reset}
          />
        )}

        {error && <UploadErrorBanner message={error} />}

        {result && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <Stat color="green" label="Created" value={result.success} Icon={CheckCircle} />
              <Stat color="blue" label="Updated" value={result.updated} Icon={FileSpreadsheet} />
              <Stat color="red" label="Failed" value={result.failed} Icon={AlertCircle} />
            </div>

            {result.errors.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <h4 className="text-sm font-semibold text-red-800">Row errors</h4>
                <div className="mt-2 max-h-40 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-red-200">
                        <th className="py-1 text-left text-red-700">Row</th>
                        <th className="py-1 text-left text-red-700">Subject</th>
                        <th className="py-1 text-left text-red-700">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.map((err, i) => (
                        <tr key={i} className="border-b border-red-100">
                          <td className="py-1 text-red-600">{err.row}</td>
                          <td className="py-1 text-red-600">{err.subject}</td>
                          <td className="py-1 text-red-600">{err.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

function Stat({ color, label, value, Icon }: { color: 'green' | 'blue' | 'red'; label: string; value: number; Icon: any }) {
  const map = {
    green: 'border-green-200 bg-green-50 text-green-700',
    blue: 'border-blue-200 bg-blue-50 text-blue-700',
    red: 'border-red-200 bg-red-50 text-red-700',
  } as const;
  const iconColor = { green: 'text-green-600', blue: 'text-blue-600', red: 'text-red-600' } as const;
  return (
    <div className={`rounded-lg border p-4 text-center ${map[color]}`}>
      <Icon className={`mx-auto h-6 w-6 ${iconColor[color]}`} />
      <p className="mt-2 text-2xl font-bold">{value}</p>
      <p className="text-xs">{label}</p>
    </div>
  );
}
